import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/management/source-brand-location-attributes';
export const getSourceBrandLocationAttributesEndpointIdentifier = endpoint;

export type ISourceBrandLocationAttributeData = {
  id: number;
  key: string;
  value: any; // This will be casted by the backend based on valueCast
  createdAt: string;
  updatedAt: string;
  sourceBrandLocation: {
    id: number;
    name: string;
    sourcePlatform: string;
    sourceExternalId: string;
    brand: {
      id: number;
      name: string;
    };
  };
};

type TData = {
  pageCount: number;
  total: number;
  data: ISourceBrandLocationAttributeData[];
};

type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    sourceBrandLocationId: TParam;
    key: TParam;
  }
];

type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSourceBrandLocationAttributesApi(options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const { getPageParams } = usePageParams();
  const { sourceBrandLocationId, key } = getPageParams();
  const { ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const params: any = {
        page,
        limit,
      };

      if (sourceBrandLocationId) {
        params.sourceBrandLocationId = sourceBrandLocationId;
      }

      if (key) {
        params.key = key;
      }

      const { data } = await http.get(endpoint, {
        params,
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        sourceBrandLocationId,
        key,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...otherOptions,
    }
  );
}
